import { ElementRef, Injectable } from "@angular/core";
import {
  Canvas2dCanvasBackend,
  IBaseLayerBackend,
  WebGlCanvasBackend,
} from "./clipping-studio-canvas.backends";
import type { ReframeConfig } from "app/js/src/domain/clipping-studio/clipping-studio-project";

/**
 * Source for one frame to be drawn: which video element and at what time (seconds).
 * Provider returns this or null (null = clear canvas, do not draw video).
 */
export interface VideoFrameSource {
  video: HTMLVideoElement;
  timeSec: number;
}

export interface WebGlViewportRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Destination rectangle for 2D canvas drawing.
 * Coordinates are canvas pixels with origin at top-left.
 */
export interface Canvas2dDestRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CanvasRenderDecision<TPayload = unknown> {
  /**
   * Optional viewport override for the video draw.
   * Coordinates are WebGL pixels with origin at bottom-left.
   */
  viewport?: WebGlViewportRect;
  /**
   * Optional destination rectangle for 2D canvas drawing.
   * Coordinates are canvas pixels with origin at top-left.
   */
  dstRect2d?: Canvas2dDestRect;
  /** Arbitrary payload passed to afterRender callback for overlay compositing. */
  payload?: TPayload;
}

/**
 * Drives one `requestAnimationFrame` loop, applies FPS throttling, then each
 * frame: `beforeDraw` (overlay/SQZ decision) → `activeBackend.drawBaseLayer` (base
 * video) → `afterRender` (main overlay + GFX, same tick). `stopRender` cancels
 * the loop and clears frame/callbacks; both built-in backends are reset.
 */
@Injectable()
export class ClippingStudioCanvasService {
  private intervalMs: number = 0;
  private lastFrameTime: number = 0;
  private rafId: number | null = null;

  /**
   * True while the service owns an active RAF loop.
   * Prefer this over checking `rafId` directly for readability.
   */
  get isRendering(): boolean {
    return this.rafId != null;
  }

  private readonly webglBackend = new WebGlCanvasBackend();
  private readonly canvas2dBackend = new Canvas2dCanvasBackend();
  private activeBackend: IBaseLayerBackend = this.webglBackend;

  private getFrame: (() => VideoFrameSource | null) | null = null;

  private beforeDrawCallback:
    | ((canvasW: number, canvasH: number) => CanvasRenderDecision)
    | null = null;
  private afterRenderCallback: ((payload?: unknown) => void) | null = null;

  /**
   * Sets the active base-layer backend; assignment only. Does not call `stopRender`,
   * `init`, or reset any handles — call `stopRender()` and re-`init` when switching modes.
   */
  setActiveBaseLayerToWebGl(): void {
    this.activeBackend = this.webglBackend;
  }

  /**
   * Sets the active base-layer to the 2D implementation; assignment only. Does not
   * call `stopRender`, `init`, or reset any handles.
   */
  setActiveBaseLayerToCanvas2d(): void {
    this.activeBackend = this.canvas2dBackend;
  }

  /**
   * Updates the current reframe configuration used by the 2D base-layer backend.
   * Assignment only; does not start/stop rendering.
   */
  setReframeConfig(cfg: ReframeConfig | null): void {
    this.canvas2dBackend.setReframeConfig(cfg);
  }

  /**
   * Set the provider that supplies the current frame (video + time) each render.
   * Call after init; pass null to clear (e.g. in stopRender).
   */
  setFrameProvider(fn: () => VideoFrameSource | null): void {
    this.getFrame = fn;
  }

  /**
   * Set a callback invoked before each video frame draw.
   * Used to decide SQZ/non-SQZ path and optionally override the video viewport.
   */
  setBeforeDrawCallback(
    fn: ((canvasW: number, canvasH: number) => CanvasRenderDecision) | null,
  ): void {
    this.beforeDrawCallback = fn;
  }

  /**
   * Set a callback invoked after each video frame draw.
   * Used to composite overlays (including SQZ) synchronized with the video render tick.
   */
  setAfterRenderCallback(fn: ((payload?: unknown) => void) | null): void {
    this.afterRenderCallback = fn;
  }

  /**
   * Bind the service to the given canvas, set the frame interval, and run the
   * active base-layer `initializeCanvas()` (WebGL2 program/texture/VAO for
   * `WebGlCanvasBackend`). No-op if already initialized for this run.
   * @param intervalMs Frame interval in milliseconds (e.g. 1000 / fps).
   */
  init(
    canvasRef: ElementRef<HTMLCanvasElement>,
    intervalMs: number,
  ): void {
    if (this.activeBackend.isInit) return;

    this.activeBackend.canvas = canvasRef.nativeElement;
    this.intervalMs = intervalMs;

    if (!this.activeBackend.initializeCanvas()) return;

    this.activeBackend.isInit = true;
  }

  startRender(): void {
    if (this.isRendering) return;
    if (!this.activeBackend.isReadyToRender) return;

    this.lastFrameTime = 0;
    this.rafId = requestAnimationFrame(this.render.bind(this));
  }

  /**
   * Stop the render loop and allow re-initialization. Call when the video preview
   * is torn down or previewUrl is cleared so that a later init() with (possibly new)
   * video/canvas refs will run instead of being skipped.
   */
  stopRender(): void {
    this.stopAnimationLoop();
    this.resetPipelines();
    this.resetBackends();
  }

  private stopAnimationLoop(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private resetPipelines(): void {
    this.getFrame = null;
    this.beforeDrawCallback = null;
    this.afterRenderCallback = null;
  }

  private resetBackends(): void {
    this.webglBackend.reset();
    this.canvas2dBackend.reset();
  }

  /**
   * Resize canvas and viewport. Call when container size changes.
   * Caller is responsible for setting video element dimensions when needed (for multiple sources, set dimensions on the active one).
   */
  resize(width: number, height: number): void {
    if (!this.activeBackend.canvas) return;

    this.activeBackend.resize(width, height);
  }

  private render(currentTime: number): void {
    if (!this.activeBackend.isReadyToRender) return;

    if (!this.activeBackend.hasValidCanvasSize) {
      this.rafId = requestAnimationFrame(this.render.bind(this));
      return;
    }

    const frame = this.getFrame?.() ?? null;

    if (frame === null) {
      // Do not clear: keep last frame to avoid black flicker when provider temporarily returns null (e.g. appended video readyState < 2).
      this.rafId = requestAnimationFrame(this.render.bind(this));
      return;
    }
    
    if (frame.video.readyState < 2) {
      // Video not ready for this frame; keep previous frame on screen to avoid flicker.
      this.rafId = requestAnimationFrame(this.render.bind(this));
      return;
    }

    const shouldDraw = currentTime - this.lastFrameTime >= this.intervalMs;

    if (shouldDraw) {
      const isPaused = frame.video.paused;
      if (isPaused && Math.abs(frame.video.currentTime - frame.timeSec) > 0.05) {
        frame.video.currentTime = frame.timeSec;
      }

      const canvasSize = this.activeBackend.getCanvasSize();
      const decision = this.beforeDrawCallback?.(
        canvasSize.width,
        canvasSize.height,
      ) ?? {};

      this.activeBackend.drawBaseLayer(frame, decision);

      this.lastFrameTime = currentTime;

      this.afterRenderCallback?.(decision.payload);
    }

    this.rafId = requestAnimationFrame(this.render.bind(this));
  }
}
